import React, { useEffect, useRef, useState } from 'react';
import MapComponent, { classifyLandUse } from '../../../components/MapComponent';
import { ZoomIcon } from '../../admin/components/navIcons';

// The right-hand list groups fields into exactly these 4 sections. Woodland
// (and any other unmatched land use) is deliberately folded into "Other" here
// — the map's legend/colouring still distinguishes Woodland separately.
const FIELD_GROUPS = [
  { key: 'custom', label: 'Hand-Drawn' },
  { key: 'arable', label: 'Arable' },
  { key: 'grassland', label: 'Grassland' },
  { key: 'other', label: 'Other' }
];

const getFieldGroup = (parcel) => {
  if (parcel.parcelRef && parcel.parcelRef.startsWith('CUSTOM_')) return 'custom';
  const landUse = classifyLandUse(parcel.name);
  return landUse === 'arable' || landUse === 'grassland' ? landUse : 'other';
};

const FieldListRow = ({ parcel, isEditingThis, editingDisabled, isSelected, onRename, onToggleIgnored, onZoom, onEditShape, onSaveEditShape, onCancelEditShape, onDeleteField }) => {
  const [nameDraft, setNameDraft] = useState(parcel.customName || '');
  const isCustom = !!parcel.parcelRef && parcel.parcelRef.startsWith('CUSTOM_');

  return (
    <div data-parcel-id={parcel._id} style={{ ...styles.row, ...(isSelected ? styles.rowSelected : {}) }}>
      <div style={styles.rowHeader}>
        <span style={styles.rowRef}>{parcel.parcelRef}</span>
        <button type="button" onClick={() => onZoom(parcel._id)} title="Zoom to this field" style={styles.iconBtn}>
          <ZoomIcon size={16} />
        </button>
      </div>
      <div style={styles.rowMeta}>{parcel.name} — {Number(parcel.area).toFixed(2)} Ha</div>

      <div style={styles.rowNameEdit}>
        <input
          type="text"
          value={nameDraft}
          onChange={(e) => setNameDraft(e.target.value)}
          placeholder="Custom field name..."
          style={styles.nameInput}
        />
        <button type="button" onClick={() => onRename(parcel._id, nameDraft)} style={styles.btnSecondary}>Save</button>
      </div>

      <label style={styles.ignoreLabel}>
        <input
          type="checkbox"
          checked={!!parcel.isIgnored}
          onChange={(e) => onToggleIgnored(parcel._id, e.target.checked)}
        />
        Ignore this field (hide from event bidding)
      </label>

      {isCustom && (
        <div style={styles.rowActions}>
          {isEditingThis ? (
            <>
              <button type="button" onClick={() => onSaveEditShape(parcel._id)} style={styles.btnPrimary}>Save Edits</button>
              <button type="button" onClick={() => onCancelEditShape(parcel._id)} style={styles.btnDangerOutline}>Discard</button>
            </>
          ) : (
            <button
              type="button"
              onClick={() => onEditShape(parcel._id)}
              disabled={editingDisabled}
              style={styles.btnSecondary}
            >
              Edit Shape
            </button>
          )}
          <button
            type="button"
            onClick={() => onDeleteField(parcel._id)}
            disabled={editingDisabled}
            style={styles.btnDangerOutline}
          >
            Delete
          </button>
        </div>
      )}
    </div>
  );
};

const FieldManagement = ({
  user,
  activeSbi,
  sbiInput,
  setSbiInput,
  previewData,
  showConfirmReset,
  isDrawingMode,
  recenterTrigger,
  managedParcels,
  zoomTarget,
  editingParcelId,
  saveEditTrigger,
  selectedFieldId,
  onMapParcelSelect,
  onPreviewFarm,
  onConfirmFarm,
  onCancelPreview,
  onRequestChangeSbi,
  onConfirmChangeSbi,
  onCancelChangeSbi,
  onStartWithoutSbi,
  onToggleDrawing,
  onDrawComplete,
  onEditComplete,
  onRecenter,
  onRename,
  onToggleIgnored,
  onZoom,
  onEditShape,
  onSaveEditShape,
  onCancelEditShape,
  onDeleteField,
  onToggleIgnoredAll
}) => {
  const listPaneRef = useRef(null);
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const toggleGroup = (key) => setCollapsedGroups(prev => ({ ...prev, [key]: !prev[key] }));

  // Map-to-list selection sync: scroll the matching row into view whenever a
  // field is clicked on the map.
  useEffect(() => {
    if (!selectedFieldId || !listPaneRef.current) return;
    const rowEl = listPaneRef.current.querySelector(`[data-parcel-id="${selectedFieldId}"]`);
    if (rowEl) rowEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [selectedFieldId]);

  return (
    <div>
      <div style={styles.headerRow}>
        <h2 style={styles.h2}>Manage My Fields</h2>

        {!activeSbi ? (
          <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
            <form onSubmit={onPreviewFarm} style={{ display: 'flex', gap: '10px' }}>
              <input
                type="text"
                placeholder="Enter SBI to Load Farm"
                value={sbiInput}
                onChange={(e) => setSbiInput(e.target.value)}
                style={{ ...styles.input, marginBottom: 0, width: '200px', border: '2px solid #3498db' }}
              />
              <button type="submit" style={styles.btnPrimary}>Preview RPA</button>
            </form>
            <span style={{ color: '#aaa', fontSize: '0.9rem', fontStyle: 'italic' }}>or</span>
            <button type="button" onClick={onStartWithoutSbi} style={{ ...styles.btnSecondary, backgroundColor: '#2c3e50' }}>
              Start Without SBI
            </button>
          </div>
        ) : (
          <div style={styles.controlsBar}>
            <span style={styles.sbiLabel}>SBI: {activeSbi}</span>
            {!showConfirmReset ? (
              <button type="button" onClick={onRequestChangeSbi} style={styles.btnDangerOutline}>Change SBI</button>
            ) : (
              <span style={{ fontSize: '0.9rem', backgroundColor: '#ffebee', padding: '5px', borderRadius: '4px' }}>
                Change Farm?
                <button type="button" onClick={onConfirmChangeSbi} style={{ ...styles.btnDanger, marginLeft: '5px', padding: '2px 8px' }}>Yes</button>
                <button type="button" onClick={onCancelChangeSbi} style={{ ...styles.btnSecondary, marginLeft: '5px', padding: '2px 8px' }}>No</button>
              </span>
            )}
            <button type="button" onClick={onRecenter} style={styles.btnSecondary} title="Reload Farm View">Recentre</button>
            <button type="button" onClick={onToggleDrawing} style={isDrawingMode ? styles.btnReversed : styles.btnPrimary}>
              {isDrawingMode ? 'Cancel Drawing' : 'Draw Field'}
            </button>
          </div>
        )}
      </div>

      {previewData && (
        <div style={styles.previewBanner}>
          <div><strong>Is this your farm?</strong> (Found {previewData.length} fields)</div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button type="button" onClick={onConfirmFarm} style={styles.btnPrimary}>Yes, Import</button>
            <button type="button" onClick={onCancelPreview} style={styles.btnReversed}>No, Cancel</button>
          </div>
        </div>
      )}

      {/* The map pane always renders — even with no SBI/preview it stays visible,
          blank, and zoomed to the farmer's registered postcode (MapComponent's
          own homePostcode fallback), rather than being replaced by empty text. */}
      <div style={styles.splitLayout}>
          <div style={styles.mapPane}>
            <MapComponent
              key={`${activeSbi}-${previewData ? 'preview' : 'live'}-${recenterTrigger}`}
              onParcelSelect={onMapParcelSelect}
              filterSbi={activeSbi}
              previewData={previewData}
              isDrawingMode={isDrawingMode}
              onDrawComplete={onDrawComplete}
              editingParcelId={editingParcelId}
              onEditComplete={onEditComplete}
              includeIgnored
              zoomTarget={zoomTarget}
              saveEditTrigger={saveEditTrigger}
              selectedParcelId={selectedFieldId}
              homePostcode={user?.postcode}
            />
          </div>

          <div style={styles.listPane} ref={listPaneRef}>
            <h3 style={styles.listHeading}>Fields ({activeSbi ? managedParcels.length : (previewData?.length || 0)})</h3>
            {!activeSbi && !previewData ? (
              <p style={styles.emptyListText}>Enter your SBI above, or draw fields manually on the map, to begin managing your land.</p>
            ) : !activeSbi ? (
              <p style={styles.emptyListText}>Confirm the import above to start managing these fields.</p>
            ) : managedParcels.length === 0 ? (
              <p style={styles.emptyListText}>No fields imported or drawn yet.</p>
            ) : (
              FIELD_GROUPS.map(group => {
                const groupParcels = managedParcels
                  .filter(p => getFieldGroup(p) === group.key)
                  .sort((a, b) => Number(b.area) - Number(a.area));

                if (groupParcels.length === 0) return null;

                // Includes ignored fields in the area total — this is the
                // farm-level land-use breakdown admins use to gauge whole-farm
                // action effectiveness, not just the actively-bid-on area.
                const groupAreaHa = groupParcels.reduce((sum, p) => sum + (Number(p.area) || 0), 0);
                const isCollapsed = !!collapsedGroups[group.key];
                const allIgnored = groupParcels.every(p => !!p.isIgnored);

                return (
                  <div key={group.key}>
                    <div style={styles.groupHeadingRow}>
                      <button type="button" onClick={() => toggleGroup(group.key)} style={styles.groupHeadingBtn}>
                        <span style={styles.groupHeadingArrow}>{isCollapsed ? '▸' : '▾'}</span>
                        {group.label} — {groupParcels.length} field{groupParcels.length !== 1 ? 's' : ''}, {groupAreaHa.toFixed(2)} Ha
                      </button>
                      <button
                        type="button"
                        onClick={() => onToggleIgnoredAll(groupParcels.map(p => p._id), !allIgnored)}
                        style={styles.groupIgnoreAllBtn}
                        title={allIgnored ? `Unignore all ${group.label} fields` : `Ignore all ${group.label} fields`}
                      >
                        {allIgnored ? 'Unignore All' : 'Ignore All'}
                      </button>
                    </div>
                    {!isCollapsed && groupParcels.map(parcel => (
                      <FieldListRow
                        key={parcel._id}
                        parcel={parcel}
                        isEditingThis={editingParcelId === parcel._id}
                        editingDisabled={!!editingParcelId}
                        isSelected={selectedFieldId === parcel._id}
                        onRename={onRename}
                        onToggleIgnored={onToggleIgnored}
                        onZoom={onZoom}
                        onEditShape={onEditShape}
                        onSaveEditShape={onSaveEditShape}
                        onCancelEditShape={onCancelEditShape}
                        onDeleteField={onDeleteField}
                      />
                    ))}
                  </div>
                );
              })
            )}
          </div>
        </div>
    </div>
  );
};

const styles = {
  headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', flexWrap: 'wrap', gap: '10px' },
  h2: { margin: 0, fontFamily: '"Plus Jakarta Sans", sans-serif', fontWeight: '600', color: '#0F172A' },
  sbiLabel: { fontWeight: 'bold', color: '#0F172A', fontFamily: '"Inter", sans-serif', whiteSpace: 'nowrap' },
  controlsBar: { display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' },

  previewBanner: { marginTop: '15px', padding: '15px', backgroundColor: '#fff3e0', border: '1px solid orange', borderRadius: '4px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },

  splitLayout: { display: 'flex', gap: '15px', marginTop: '15px', height: 'calc(100vh - 220px)' },
  mapPane: { flex: 2, border: '1px solid #ddd', borderRadius: '8px', overflow: 'hidden', position: 'relative', minHeight: 0 },
  listPane: { flex: 1, backgroundColor: '#FFFFFF', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', padding: '15px', overflowY: 'auto', minHeight: 0, boxSizing: 'border-box' },
  // sticky so the "Fields (##)" count stays pinned above the scrolling list —
  // needs its own background or content scrolling underneath would show through.
  listHeading: { marginTop: 0, fontFamily: '"Plus Jakarta Sans", sans-serif', fontWeight: '600', color: '#0D9488', borderBottom: '2px solid #0D9488', paddingBottom: '5px', position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#FFFFFF' },
  groupHeadingRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', margin: '14px 0 8px 0', padding: '0 0 4px 0', borderBottom: '1px solid #eee' },
  groupHeadingBtn: { display: 'flex', alignItems: 'center', gap: '6px', flex: 1, background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', fontSize: '16px', fontWeight: '700', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.03em', padding: 0, fontFamily: '"Inter", sans-serif' },
  groupHeadingArrow: { color: '#0D9488', fontSize: '0.7rem', width: '10px', display: 'inline-block' },
  groupIgnoreAllBtn: { flexShrink: 0, background: 'none', border: '1px solid #0D9488', color: '#0D9488', borderRadius: '4px', padding: '4px 10px', fontSize: '0.75rem', fontWeight: '600', cursor: 'pointer', fontFamily: '"Inter", sans-serif', textTransform: 'none', letterSpacing: 'normal' },
  emptyListText: { color: '#475569', fontSize: '0.9rem', fontFamily: '"Inter", sans-serif' },

  row: { border: '1px solid #eee', borderRadius: '6px', padding: '12px', marginBottom: '10px', backgroundColor: '#F8FAFC' },
  rowSelected: { border: '2px solid #0D9488', backgroundColor: 'rgba(13, 148, 136, 0.08)' },
  rowHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  rowRef: { fontWeight: 'bold', fontSize: '0.85rem', color: '#0D9488', fontFamily: '"Inter", sans-serif' },
  rowMeta: { fontSize: '0.75rem', color: '#475569', fontFamily: '"Inter", sans-serif', marginTop: '2px' },
  rowNameEdit: { display: 'flex', gap: '8px', marginTop: '8px' },
  nameInput: { flex: 1, padding: '6px 8px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '0.85rem', fontFamily: '"Inter", sans-serif', color: '#0F172A' },
  ignoreLabel: { display: 'flex', alignItems: 'center', gap: '6px', marginTop: '8px', fontSize: '0.8rem', color: '#475569', fontFamily: '"Inter", sans-serif', cursor: 'pointer' },
  rowActions: { display: 'flex', gap: '8px', marginTop: '8px' },

  iconBtn: { background: 'none', border: 'none', cursor: 'pointer', color: '#0D9488', padding: '2px', display: 'flex', alignItems: 'center' },
  input: { padding: '10px', borderRadius: '4px', border: '1px solid #ddd', width: '100%', boxSizing: 'border-box', fontSize: '0.9rem', fontFamily: '"Inter", sans-serif', color: '#0F172A' },
  btnPrimary: { padding: '10px 15px', backgroundColor: '#0D9488', color: '#FFFFFF', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', fontFamily: '"Inter", sans-serif' },
  btnReversed: { padding: '8px 12px', backgroundColor: '#FFFFFF', color: '#0D9488', border: '1px solid #0D9488', borderRadius: '4px', cursor: 'pointer', fontFamily: '"Inter", sans-serif', fontWeight: 'bold' },
  btnSecondary: { padding: '8px 12px', backgroundColor: '#0D9488', color: '#FFFFFF', border: 'none', borderRadius: '4px', cursor: 'pointer', fontFamily: '"Inter", sans-serif' },
  btnDanger: { padding: '8px 12px', backgroundColor: '#0D9488', color: '#F59E0B', border: 'none', borderRadius: '4px', cursor: 'pointer', fontFamily: '"Inter", sans-serif', fontWeight: 'bold' },
  btnDangerOutline: { padding: '8px 12px', backgroundColor: '#FFFFFF', color: '#F59E0B', border: '1px solid #F59E0B', borderRadius: '4px', cursor: 'pointer', fontFamily: '"Inter", sans-serif', fontWeight: 'bold', fontSize: '0.85rem' }
};

export default FieldManagement;
